import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Divider, Typography, Button, TextField, MenuItem, InputAdornment, Alert, Chip, Pagination, LinearProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { Store } from '../../../types';
import { getActiveMenus, getInactiveMenus, getAllOptionTypesWithValues } from '../../../services/api';
import { MenusAPIResponse, StoreMenuResult, StoreError } from '../../../types/menuApi';
import { generateErrorSummary, hasOnlyNonCriticalErrors } from '../../../utils/menuErrorHandling';
import { promisePool } from '../../../utils/promisePool';
import MenuInfo from './MenuInfo';
import { MenuState as Menu } from './types';

interface MenuViewerProps {
  selectedStores?: Store[];
  menus?: Menu[];
  verifyMode?: boolean;
}

const MenuViewer: React.FC<MenuViewerProps> = ({
  selectedStores,
  menus: initialMenus,
  verifyMode = false,
}) => {
  const [menus, setMenus] = useState<Menu[] | undefined>(initialMenus);
  const [filteredMenus, setFilteredMenus] = useState<Menu[] | undefined>(initialMenus);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState<{ completed: number; total: number } | null>(null);
  const [optionValueCodeToName, setOptionValueCodeToName] = useState<Record<string, string>>({});
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [currentView, setCurrentView] = useState<'none' | 'active' | 'inactive'>('none');

  // New state for API response handling
  const [apiResponse, setApiResponse] = useState<MenusAPIResponse | null>(null);
  const [errors, setErrors] = useState<StoreError[]>([]);
  const [successCount, setSuccessCount] = useState(0);
  const [failureCount, setFailureCount] = useState(0);
  const [showMetrics, setShowMetrics] = useState(false);

  // Client-side pagination state (for displaying menus)
  const [currentPage, setCurrentPage] = useState(1); // 1-based for UI
  const [menusPerPage, setMenusPerPage] = useState(10); // Default 10 menus per page
  const menusPerPageOptions = [5, 10, 25, 50];

  // Filter state
  type FilterType = 'all' | 'product' | 'bundle' | 'issues-product' | 'issues-bundle';
  const [filter, setFilter] = useState({
    type: 'all' as FilterType,
    name: '',
    code: '',
    price: '',
  });
  const [filterDraft, setFilterDraft] = useState(filter);
  const [expandedMenus, setExpandedMenus] = useState<{ [storeNumber: string]: boolean }>({});

  const consolidateResponses = (responses: MenusAPIResponse[], totalRequested: number) => {
    let allSuccessResults: any[] = [];
    let allFailedResults: any[] = [];
    
    responses.forEach(response => {
      if (response.success) allSuccessResults = allSuccessResults.concat(response.success);
      if (response.failed) allFailedResults = allFailedResults.concat(response.failed);
    });
    
    return { allSuccessResults, allFailedResults };
  };

  const applyMenuResults = (allSuccessResults: any[], allFailedResults: any[], totalRequested: number) => {
    const consolidatedResponse = {
      success: allSuccessResults,
      failed: allFailedResults,
      metadata: {
        successCount: allSuccessResults.length,
        failureCount: allFailedResults.length,
        totalRequested,
        partialSuccess: allFailedResults.length > 0 && allSuccessResults.length > 0,
        processingTime: '0ms',
        failuresByType: {}
      },
      count: totalRequested,
      pages: 1,
      page: 0
    };
    
    setApiResponse(consolidatedResponse);
    setSuccessCount(allSuccessResults.length);
    setFailureCount(allFailedResults.length);
    setShowMetrics(true);
    setCurrentPage(1);
    
    const transformedMenus: Menu[] = allSuccessResults.map((result: StoreMenuResult) => ({
      storeNumber: result.storeNumber,
      product: result.menuData?.products || [],
      bundle: result.menuData?.bundles || [],
      categories: result.menuData?.categories || [],
    }));
    
    transformedMenus.sort((a, b) =>
      parseInt(a.storeNumber, 10) - parseInt(b.storeNumber, 10)
    );
    
    setMenus(transformedMenus);
    
    if (allFailedResults.length > 0) {
      setErrors(allFailedResults);
      setShowMetrics(true);
    }
  };

  useEffect(() => {
    const fetchOptionTypes = async () => {
      try {
        setIsLoadingOptions(true);
        const optionsMap = await getAllOptionTypesWithValues();
        setOptionValueCodeToName(optionsMap);
      } catch (error) {
        console.error('Failed to fetch option types:', error);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchOptionTypes();
  }, []);

  useEffect(() => {
  const fetchMenus = async () => {
    if (!selectedStores || selectedStores.length === 0) return;

    setIsLoading(true);
    setErrors([]);
    setShowMetrics(false);
    setLoadingProgress(null);

    try {
      const storeNumbers = selectedStores.map((s) => s.storeNumber);
      
      const chunkSize = 5; // Must stay at 5 to avoid AWS API Gateway payload limits
      const storeChunks: string[][] = [];
      for (let i = 0; i < storeNumbers.length; i += chunkSize) {
        storeChunks.push(storeNumbers.slice(i, i + chunkSize));
      }
      
      const tasks = storeChunks.map(chunk => () =>
        getInactiveMenus(chunk, 0, chunk.length)
      );
      
      const responses = await promisePool(tasks, 4, (completed, total) => {
        setLoadingProgress({ completed, total });
      });
      
      const { allSuccessResults, allFailedResults } = consolidateResponses(responses, storeNumbers.length);
      applyMenuResults(allSuccessResults, allFailedResults, storeNumbers.length);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch menus';
      setErrors([{
        storeNumber: 'GENERAL',
        errorCode: 'FETCH_ERROR',
        errorType: 'SYSTEM_ERROR',
        message: errorMessage,
        details: undefined,
        timestamp: new Date().toISOString()
      }]);
      setShowMetrics(true);
      console.error('Error fetching menus:', error);
    } finally {
      setIsLoading(false);
      setLoadingProgress(null);
    }
  };
  
  if (verifyMode && selectedStores && !initialMenus) {
    fetchMenus();
  }
}, [selectedStores, verifyMode, initialMenus]);

  // Filtering logic
  const filterMenus = (menus: Menu[], filterState: { type: FilterType, name: string, code: string, price: string }) => {
    if (!menus) return [];
    return menus.map(menu => {
      let filteredProducts = menu.product || [];
      let filteredBundles = menu.bundle || [];
      
      // Filter by type
      if (filterState.type === 'product') filteredBundles = [];
      if (filterState.type === 'bundle') filteredProducts = [];
      
      // Filter by name/code/price
      if (filterState.name) {
        filteredProducts = filteredProducts.filter((p: any) => p.name?.toLowerCase().includes(filterState.name.toLowerCase()));
        filteredBundles = filteredBundles.filter((b: any) => b.name?.toLowerCase().includes(filterState.name.toLowerCase()));
      }
      if (filterState.code) {
        filteredProducts = filteredProducts.filter((p: any) => p.productCode?.toLowerCase().includes(filterState.code.toLowerCase()));
        filteredBundles = filteredBundles.filter((b: any) => b.bundleCode?.toLowerCase().includes(filterState.code.toLowerCase()));
      }
      if (filterState.price) {
        filteredProducts = filteredProducts.filter((p: any) =>
          Array.isArray(p.variants) &&
          p.variants.some((v: any) => v.price && (v.price.amount + '').includes(filterState.price))
        );
        filteredBundles = filteredBundles.filter((b: any) => b.price && (b.price.amount + '').includes(filterState.price));
      }
      
      // Filter by issues if set
      if (filterState.type === 'issues-product') {
        filteredBundles = [];
        filteredProducts = filteredProducts.filter((p: any) => JSON.stringify(p).includes('9999'));
      }
      if (filterState.type === 'issues-bundle') {
        filteredProducts = [];
        filteredBundles = filteredBundles.filter((b: any) => b.price?.amount === 9999);
      }
      
      return {
        ...menu,
        product: filteredProducts,
        bundle: filteredBundles,
      };
    }).filter(menu => (menu.product.length > 0 || menu.bundle.length > 0));
  };

  // Apply filtering whenever menus or filter changes
  useEffect(() => {
    if (menus) {
      setFilteredMenus(filterMenus(menus, filter));
    }
  }, [menus, filter]);

  // Update filterDraft when filter changes (for reset functionality)
  useEffect(() => {
    setFilterDraft(filter);
  }, [filter]);

  const handleFetchMenus = async (type: 'active' | 'inactive') => {
    if (!selectedStores || selectedStores.length === 0) return;
    
    setIsLoading(true);
    setErrors([]);
    setShowMetrics(false);
    setLoadingProgress(null);

    try {
      const storeNumbers = selectedStores.map((s) => s.storeNumber);
      
      const chunkSize = 5; // Must stay at 5 to avoid AWS API Gateway payload limits
      const storeChunks: string[][] = [];
      for (let i = 0; i < storeNumbers.length; i += chunkSize) {
        storeChunks.push(storeNumbers.slice(i, i + chunkSize));
      }
      
      const fetchFn = type === 'inactive' ? getInactiveMenus : getActiveMenus;
      const tasks = storeChunks.map(chunk => () =>
        fetchFn(chunk, 0, chunk.length)
      );
      
      const responses = await promisePool(tasks, 4, (completed, total) => {
        setLoadingProgress({ completed, total });
      });
      
      const { allSuccessResults, allFailedResults } = consolidateResponses(responses, storeNumbers.length);
      applyMenuResults(allSuccessResults, allFailedResults, storeNumbers.length);
      setCurrentView(type);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch menus';
      setErrors([{
        storeNumber: 'GENERAL',
        errorCode: 'FETCH_ERROR',
        errorType: 'SYSTEM_ERROR',
        message: errorMessage,
        details: undefined,
        timestamp: new Date().toISOString()
      }]);
      setShowMetrics(true);
      console.error('Error fetching menus:', error);
    } finally {
      setIsLoading(false);
      setLoadingProgress(null);
    }
  };

  // Calculate client-side pagination for filtered menus
  const totalMenus = filteredMenus?.length || 0;
  const totalPages = Math.ceil(totalMenus / menusPerPage);
  const startIndex = (currentPage - 1) * menusPerPage;
  const endIndex = startIndex + menusPerPage;
  const paginatedMenus = filteredMenus?.slice(startIndex, endIndex) || [];

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleMenusPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMenusPerPage(parseInt(event.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Apply filter function
  const applyFilter = () => {
    setFilter(filterDraft);
    setCurrentPage(1); // Reset to first page when applying filters
    // Force immediate update of filtered menus
    if (menus) {
      setFilteredMenus(filterMenus(menus, filterDraft));
    }
  };

  // Issues filter handler
  const handleIssuesFilter = (type: 'product' | 'bundle') => {
    const targetType = (type === 'product' ? 'issues-product' : 'issues-bundle') as FilterType;
    let newFilter;
    if (filter.type === targetType) {
      // Toggle off
      newFilter = { type: 'all' as FilterType, name: '', code: '', price: '' };
    } else {
      newFilter = { type: targetType, name: '', code: '', price: '' };
    }
    
    setFilter(newFilter);
    setFilterDraft(newFilter);
    setCurrentPage(1); // Reset to first page when changing filters
    
    // Force immediate update of filtered menus
    if (menus) {
      setFilteredMenus(filterMenus(menus, newFilter));
    }
  };

  // Reset filter
  const resetFilter = () => {
    const reset = { type: 'all' as FilterType, name: '', code: '', price: '' };
    setFilter(reset);
    setFilterDraft(reset);
    setCurrentPage(1); // Reset to first page when resetting filters
    
    // Force immediate update of filtered menus
    if (menus) {
      setFilteredMenus(filterMenus(menus, reset));
    }
  };

  // Expand/collapse all functionality
  const handleExpandAll = () => {
    const newState: { [storeNumber: string]: boolean } = {};
    (filteredMenus || []).forEach((menu) => {
      newState[menu.storeNumber] = true;
    });
    setExpandedMenus(newState);
  };

  const handleCollapseAll = () => {
    const newState: { [storeNumber: string]: boolean } = {};
    (filteredMenus || []).forEach((menu) => {
      newState[menu.storeNumber] = false;
    });
    setExpandedMenus(newState);
  };

  if (isLoading || isLoadingOptions) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 1.5 }}>
        <CircularProgress />
        {loadingProgress && (
          <>
            <Typography variant="body2" color="text.secondary">
              Fetching menus… {loadingProgress.completed} / {loadingProgress.total} batches
            </Typography>
            <Box sx={{ width: '60%' }}>
              <LinearProgress
                variant="determinate"
                value={(loadingProgress.completed / loadingProgress.total) * 100}
              />
            </Box>
          </>
        )}
      </Box>
    );
  }

  // In review mode (not verifyMode), show fetch buttons if no menus loaded
  if (!verifyMode && (!menus || menus.length === 0)) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            data-testid="fetchInactiveMenusButton"
            onClick={() => {
              setCurrentPage(1); // Reset to first page
              handleFetchMenus('inactive');
            }}
            disabled={!selectedStores || selectedStores.length === 0 || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            Fetch Inactive Menus
          </Button>
          <Button
            variant="contained"
            color="secondary"
            data-testid="fetchActiveMenusButton"
            onClick={() => {
              setCurrentPage(1); // Reset to first page
              handleFetchMenus('active');
            }}
            disabled={!selectedStores || selectedStores.length === 0 || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            Fetch Active Menus
          </Button>
        </Box>
        {(!selectedStores || selectedStores.length === 0) && (
          <Typography color="text.secondary">
            Please select stores to fetch menu data.
          </Typography>
        )}
      </Box>
    );
  }

  if (!menus || menus.length === 0) {
    return <Typography sx={{ p: 3 }}>No menus to display.</Typography>;
  }

  return (
    <Box>
      {/* Only show fetch buttons in review mode (not verifyMode) */}
      {!verifyMode && (
        <Box sx={{ mb: 2, ml: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2 }}>
            <Button
              variant={currentView === 'inactive' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => {
                setCurrentPage(1); // Reset to first page
                handleFetchMenus('inactive');
              }}
              disabled={isLoading}
            >
              {currentView === 'inactive' ? 'Inactive Menus' : 'Fetch Inactive Menus'}
            </Button>
            <Button
              variant={currentView === 'active' ? 'contained' : 'outlined'}
              color="secondary"
              onClick={() => {
                setCurrentPage(1); // Reset to first page
                handleFetchMenus('active');
              }}
              disabled={isLoading}
            >
              {currentView === 'active' ? 'Active Menus' : 'Fetch Active Menus'}
            </Button>
          </Box>
          {/* Fixed height container to prevent layout shift */}
          <Box sx={{ height: '24px', mb: 2 }} data-testid='currentViewIndicator'>
            {currentView !== 'none' && (
              <Typography variant="body2" color="text.secondary">
                Currently viewing: {currentView === 'active' ? 'Active Menus' : 'Inactive Menus'}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      
      {/* Success/Error Metrics Display */}
      {showMetrics && apiResponse && (
        <Box sx={{ mt: 2, mb: 2, ml: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }} data-testid='metricsDisplay'>
            {successCount > 0 && (
              <Chip 
                label={`✅ ${successCount} successful`} 
                color="success" 
                size="small"
              />
            )}
            {failureCount > 0 && (
              <Chip 
                label={`❌ ${failureCount} failed`} 
                color="error" 
                size="small"
              />
            )}
          </Box>
          
          {errors.length > 0 && (
            <Alert 
              severity={hasOnlyNonCriticalErrors(errors) ? "warning" : "error"}
              icon={hasOnlyNonCriticalErrors(errors) ? <WarningIcon /> : <ErrorIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                <strong>Issues found:</strong> {generateErrorSummary(errors)}
              </Typography>
            </Alert>
          )}
        </Box>
      )}
      
      {/* Show filtering controls for both modes when menus are available */}
      {menus && menus.length > 0 && (
        <Box sx={{ mb: 2, ml: 2 }}>
          {/* Filter form with Enter key support */}
          <form
            onSubmit={e => {
              e.preventDefault();
              applyFilter();
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                  select
                  value={menusPerPage}
                  onChange={handleMenusPerPageChange}
                  size="small"
                  sx={{ minWidth: 100 }}
                  label="Per page"
                >
                  {menusPerPageOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField
                select
                label="Type"
                value={filterDraft.type.startsWith('issues') ? 'all' : filterDraft.type}
                onChange={e => setFilterDraft({ ...filterDraft, type: e.target.value as FilterType })}
                size="small"
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="product">Product</MenuItem>
                <MenuItem value="bundle">Bundle</MenuItem>
              </TextField>
              <TextField
                label="Name"
                value={filterDraft.name}
                onChange={e => setFilterDraft({ ...filterDraft, name: e.target.value })}
                size="small"
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Code"
                value={filterDraft.code}
                onChange={e => setFilterDraft({ ...filterDraft, code: e.target.value })}
                size="small"
                sx={{ minWidth: 150 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Price"
                value={filterDraft.price}
                onChange={e => setFilterDraft({ ...filterDraft, price: e.target.value })}
                size="small"
                sx={{ minWidth: 120 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
                <Button type="submit" variant="contained" size="small" sx={{ textTransform: 'none' }}>
                  Filter
                </Button>
                <Button variant="outlined" size="small" onClick={resetFilter} sx={{ textTransform: 'none' }}>
                  Reset
                </Button>
              </Box>
            </Box>
          </form>
          
          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="text" size="small" onClick={handleExpandAll} sx={{ textTransform: 'none' }}>
              Expand All
            </Button>
            <Button variant="text" size="small" onClick={handleCollapseAll} sx={{ textTransform: 'none' }}>
              Collapse All
            </Button>
            <Button 
              variant={filter.type === 'issues-product' ? 'contained' : 'outlined'} 
              color="error" 
              size="small" 
              onClick={() => handleIssuesFilter('product')} 
              sx={{ textTransform: 'none' }}
            >
              Products with Issues
            </Button>
            <Button 
              variant={filter.type === 'issues-bundle' ? 'contained' : 'outlined'} 
              color="error" 
              size="small" 
              onClick={() => handleIssuesFilter('bundle')} 
              sx={{ textTransform: 'none' }}
            >
              Bundles with Issues
            </Button>
          </Box>
        </Box>
      )}
      
      <Divider />
      
      {/* Small line break after divider */}
      <Box sx={{ mb: 2 }} />
      
      {/* Render filtered menus */}
      {filteredMenus && filteredMenus.length > 0 ? (
        <>
          {paginatedMenus.map((menu) => (
            <Box key={`${menu.storeNumber}-${JSON.stringify(filter)}`} sx={{ mb: 2 }}>
              <MenuInfo 
                menu={menu} 
                optionValueCodeToName={optionValueCodeToName}
                expanded={!!expandedMenus[menu.storeNumber]}
                onExpandedChange={(expanded) => setExpandedMenus(prev => ({ ...prev, [menu.storeNumber]: expanded }))}
              />
            </Box>
          ))}
          
          {/* Client-side Pagination Controls */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                disabled={isLoading}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      ) : menus && menus.length > 0 ? (
        <Typography sx={{ p: 3 }}>No menus match the current filter criteria.</Typography>
      ) : (
        <Typography sx={{ p: 3 }}>
          {verifyMode ? 'No menus to display.' : 'No menus to display. Use the buttons above to fetch menu data.'}
        </Typography>
      )}
    </Box>
  );
};

export default MenuViewer;